import {
  memo,
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";

import { useCartLine } from "@shopify/hydrogen-react";
import {
  type CartLine,
  type Product,
  type SelectedOption,
} from "@shopify/hydrogen-react/storefront-api-types";
import { preload } from "swr";

import { useCartActiveProduct } from "@cart/CartActiveProductContext";
import { useCartGiftWithPurchase } from "@cart/CartGiftWithPurchaseContext";
import { CartItemView } from "@cart/CartItem/CartItem.view";
import { storefrontAPIFetcher } from "@react/hooks/fetchers";
import { useProduct, useProductParams } from "@react/hooks/useProduct";
import { isDefaultOption } from "@react/utilities";
import {
  STOREFRONT_CART_ATTRIBUTES,
  CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS,
} from "@root/tablescape/constants";
import { TablescapeContext, TablescapeEvent } from "@tablescape/context";
import { type TablescapeState } from "@tablescape/state";
import {
  checkIfNonEditableProductVendor,
  formatMerchandise,
  getGiftWithPurchaseAttributeValue,
  getCartLineAttributeValue,
  getIntelligemsComparePriceByVariantId,
  getShopifyGlobalId,
  hasCartLineAttribute,
  hasIntelligemsLineItemAttribute,
} from "@tablescape/utilities";

type ActiveItemDetails = {
  productId: Product["id"];
  ui: Partial<TablescapeState["ui"]>;
};

type Props = {
  bundleValueText: string;
  cartLinkTo?: string;
  cartProductGiftItemButtonEditTextLabel?: string;
  cartProductGiftItemButtonTextLabel?: string;
  cartProductPage?: string;
  linkAriaLabel?: string;
  selectedGiftsWithPurchase: CartLine[] | undefined;
};

const CartItemBase = ({
  bundleValueText,
  cartLinkTo,
  cartProductGiftItemButtonEditTextLabel,
  cartProductGiftItemButtonTextLabel,
  cartProductPage,
  linkAriaLabel,
  selectedGiftsWithPurchase,
}: Props) => {
  const [activeItemDetails, setActiveItemDetails] = useState<
    ActiveItemDetails | undefined
  >();

  const {
    cart,
    ui: { cartDrawerAnimating, cartLineAdding, cartLineUpdating },
  } = useSyncExternalStore<TablescapeState>(
    TablescapeContext.onAll,
    TablescapeContext.getSnapshot
  );
  const { toggleCartDrawer } = useCartActiveProduct();
  const {
    attributes,
    cost,
    id: cartLineId,
    merchandise: unformattedMerchandise,
    quantity,
  } = useCartLine();

  const { relatedCartLinesUpdating, setActiveCartLineId } =
    useCartGiftWithPurchase();
  const { product } = useProduct(activeItemDetails?.productId);

  useEffect(() => {
    const {
      ui: { cartLineUpdating },
    } = TablescapeContext.getSnapshot();

    const isProductLoading = !!(activeItemDetails?.productId && !product?.id);

    if (cartLineUpdating === isProductLoading) {
      return;
    }

    TablescapeContext.emit(TablescapeEvent.TOGGLE_CART_LOADING_STATE, {
      ui: {
        cartLineUpdating: isProductLoading,
      },
    });
  }, [activeItemDetails?.productId, product?.id]);

  const merchandise = useMemo(
    () =>
      unformattedMerchandise
        ? formatMerchandise(unformattedMerchandise)
        : undefined,
    [unformattedMerchandise]
  );

  const selectedOptions = useMemo(
    () =>
      merchandise?.selectedOptions?.filter(
        (o): o is Partial<SelectedOption> => !!o
      ),
    [merchandise?.selectedOptions]
  );

  const selectedVariant = useMemo(
    () =>
      product?.variants?.find((variant) => {
        return variant.selectedOptions?.every((option) => {
          return selectedOptions?.find(
            (selectedOption) =>
              selectedOption.name === option.name &&
              selectedOption.value === option.value
          );
        });
      }) ?? product?.variants?.[0],
    [product?.variants, selectedOptions]
  );

  const productVariantDrawerMetadata = useMemo(() => {
    if (!product || cartDrawerAnimating || !activeItemDetails?.productId) {
      return undefined;
    }

    return {
      activeCartLineId: cartLineId,
      activeOptions: selectedOptions,
      activeProduct: product,
      activeVariant: selectedVariant,
      isGiftWithPurchase: false,
      isProductUpdate: true,
    };
  }, [
    activeItemDetails?.productId,
    cartDrawerAnimating,
    cartLineId,
    product,
    selectedOptions,
    selectedVariant,
  ]);

  useEffect(() => {
    if (!productVariantDrawerMetadata || !activeItemDetails?.productId) {
      return;
    }

    requestAnimationFrame(() => {
      toggleCartDrawer(activeItemDetails.ui, productVariantDrawerMetadata);
      setActiveItemDetails(undefined);
    });
  }, [activeItemDetails, productVariantDrawerMetadata, toggleCartDrawer]);

  const shippingNote = useMemo(
    () => merchandise?.shippingNote?.value ?? null,
    [merchandise]
  );

  const isVariantBundleKit = useMemo(
    () =>
      hasCartLineAttribute(
        { attributes },
        STOREFRONT_CART_ATTRIBUTES.VARIANT_BUNDLE_KIT
      ),
    [attributes]
  );

  const onlineStoreUrl = useMemo(() => {
    if (checkIfNonEditableProductVendor(merchandise?.product?.vendor ?? "")) {
      return undefined;
    }

    if (isVariantBundleKit) {
      return (
        getCartLineAttributeValue(
          { attributes },
          STOREFRONT_CART_ATTRIBUTES.VARIANT_BUNDLE_URL
        ) ?? "/"
      );
    }

    return merchandise?.product?.onlineStoreUrl ?? "/";
  }, [
    attributes,
    isVariantBundleKit,
    merchandise?.product?.onlineStoreUrl,
    merchandise?.product?.vendor,
  ]);

  const isPricedWithIntelligems = useMemo(
    () => hasIntelligemsLineItemAttribute({ attributes }),
    [attributes]
  );

  const intelligemsComparePrice = useMemo(() => {
    if (!isPricedWithIntelligems || !merchandise?.id || !quantity) {
      return null;
    }

    const igPriceByVariantId = getIntelligemsComparePriceByVariantId(
      merchandise.id
    );

    if (!igPriceByVariantId) {
      return null;
    }

    return (igPriceByVariantId * quantity).toFixed(1);
  }, [isPricedWithIntelligems, merchandise?.id, quantity]);

  const compareAtTotalAmount = useMemo(() => {
    if (isPricedWithIntelligems && intelligemsComparePrice) {
      const compareAtAmount =
        cost?.compareAtAmountPerQuantity ?? cost?.totalAmount;

      return {
        currencyCode: compareAtAmount?.currencyCode ?? "USD",
        amount: intelligemsComparePrice,
      };
    }

    if (cost?.compareAtAmountPerQuantity?.amount && quantity) {
      return {
        currencyCode: cost.compareAtAmountPerQuantity.currencyCode ?? "USD",
        ...cost.compareAtAmountPerQuantity,
        amount: (
          parseInt(cost.compareAtAmountPerQuantity.amount) * quantity
        ).toString(),
      };
    } else if (
      merchandise?.price?.amount &&
      quantity &&
      cost?.totalAmount?.amount &&
      parseInt(merchandise.price.amount) * quantity >
        parseInt(cost.totalAmount.amount)
    ) {
      return {
        currencyCode: cost.totalAmount.currencyCode ?? "USD",
        ...cost.totalAmount,
        amount: (parseInt(merchandise.price.amount) * quantity).toString(),
      };
    }
  }, [
    cost?.compareAtAmountPerQuantity,
    cost?.totalAmount,
    intelligemsComparePrice,
    isPricedWithIntelligems,
    merchandise?.price?.amount,
    quantity,
  ]);

  const addingLineIsInCart = useMemo(() => {
    return !!cart?.lines?.some(
      (line) =>
        line?.id &&
        getShopifyGlobalId("ProductVariant", line.id) === merchandise?.id
    );
  }, [cart?.lines, merchandise?.id]);

  const validSelectedOptions = useMemo(
    () =>
      merchandise?.selectedOptions
        ?.filter((o): o is SelectedOption => !!o)
        .filter((o) => !isDefaultOption(o)),
    [merchandise?.selectedOptions]
  );

  const cartSpendingGiftBadge = useMemo(
    () =>
      getGiftWithPurchaseAttributeValue(
        { attributes },
        CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS.GIFT_BADGE
      ) || "",
    [attributes]
  );

  const isEditable = useMemo(
    () =>
      !!validSelectedOptions?.length &&
      !checkIfNonEditableProductVendor(merchandise?.product?.vendor ?? ""),
    [merchandise?.product?.vendor, validSelectedOptions?.length]
  );

  const isQuantityEditable = useMemo(
    () =>
      !cartSpendingGiftBadge &&
      !checkIfNonEditableProductVendor(merchandise?.product?.vendor ?? ""),
    [cartSpendingGiftBadge, merchandise?.product?.vendor]
  );

  const handlePreloadProduct = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.preventDefault();

      if (!merchandise?.product?.id) {
        return;
      }

      preload(
        [...useProductParams, { productId: merchandise.product.id }],
        storefrontAPIFetcher
      );
    },
    [merchandise?.product?.id]
  );

  const handleOpenProductVariantDrawer = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.preventDefault();

      if (!cartLineId || !merchandise?.product?.id) {
        return;
      }

      setActiveCartLineId(cartLineId);
      setActiveItemDetails({
        productId: merchandise.product.id,
        ui: { productVariantDrawerOpen: true },
      });
    },
    [cartLineId, merchandise?.product?.id, setActiveCartLineId]
  );

  const handleOpenGiftWithPurchaseDrawer = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.preventDefault();

      if (!cartLineId || !merchandise?.product?.id) {
        return;
      }

      setActiveCartLineId(cartLineId);
      setActiveItemDetails({
        productId: merchandise.product.id,
        ui: { giftWithPurchaseDrawerOpen: true },
      });
    },
    [cartLineId, merchandise?.product?.id, setActiveCartLineId]
  );

  return (
    <CartItemView
      badgeText={cartSpendingGiftBadge}
      bundleValueText={bundleValueText}
      cartLineUpdating={
        relatedCartLinesUpdating ||
        cartLineUpdating ||
        (cartLineAdding && addingLineIsInCart)
      }
      cartLinkTo={cartLinkTo}
      cartProductGiftItemButtonEditTextLabel={
        cartProductGiftItemButtonEditTextLabel
      }
      cartProductGiftItemButtonTextLabel={cartProductGiftItemButtonTextLabel}
      cartProductPage={cartProductPage}
      compareAtPriceDisplay={merchandise?.compareAtPriceDisplay?.value}
      compareAtTotalAmount={compareAtTotalAmount}
      cost={cost}
      handleOpenGiftWithPurchaseDrawer={handleOpenGiftWithPurchaseDrawer}
      handleOpenProductVariantDrawer={handleOpenProductVariantDrawer}
      handlePreloadProduct={handlePreloadProduct}
      isEditable={isEditable}
      isQuantityEditable={isQuantityEditable}
      linkAriaLabel={linkAriaLabel}
      merchandise={merchandise}
      onlineStoreUrl={onlineStoreUrl}
      selectedGiftsWithPurchase={selectedGiftsWithPurchase}
      selectedOptions={validSelectedOptions}
      shippingNote={shippingNote}
    />
  );
};

export const CartItem = memo(CartItemBase);
